import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import { motion, useAnimation } from "framer-motion";
import { setTileCoords, unsetUserTileCoords } from "../redux/dominoSlice";
import { startDrag, stopDrag, startDrop } from "../redux/dragNdropSlice";
import { getNumbers } from "../util/tileOperations";
import DropPlaceholder from "./DropPlaceholder";

const DominoTile = styled(motion.div)`
  margin: ${({ variant }) => (variant === "dimmed" ? "0px" : `0 0 5px 0`)};
  margin: ${({ size }) => size === "sm" && `1px 0`};
  visibility: hidden;
  font-size: ${({ size }) =>
    size === "sm" ? css`2rem` : size === "md" ? css`3.5rem` : css`5rem`};
  ${({ drag }) =>
    drag &&
    css`
      cursor: move;
    `}
  color: ${({ variant }) =>
    variant === "selected"
      ? css`#9ec4f1`
      : variant === "dimmed"
      ? css`#2e85ec`
      : variant === "droppable"
      ? css`aquamarine`
      : css`#2e85ec`};
`;

const Tile = ({
  tile = {
    id: "66",
    isRotated: false,
    lastCoords: {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    },
  },
  size = "md",
  tileStyle = "vertical",
  draggable = false,
  first = false,
  last = false,
  faceDown = false,
  duration = 0.3,
  variant = null,
  className,
}) => {
  const ref = useRef();
  const dispatch = useDispatch();
  const { firstInPlayline } = useSelector((state) => state.domino);
  const controls = useAnimation();

  useEffect(() => {
    if (ref?.current) {
      ref.current.style.visibility = "visible";
      if (tile.lastCoords) {
        const coords = ref.current.getBoundingClientRect();
        controls.set({
          x: tile.lastCoords.x - coords.x,
          y: tile.lastCoords.y - coords.y,
        });
        controls
          .start({
            x: 0,
            y: 0,
            transition: {
              duration,
              ease: "easeOut",
            },
          })
          .then(() => {
            if (draggable) {
              dispatch(unsetUserTileCoords(tile));
            } else {
              const rect = ref?.current?.getBoundingClientRect();
              if (rect) {
                const { x, y, width, height } = rect;
                dispatch(
                  setTileCoords({
                    tile,
                    lastCoords: {
                      x,
                      y,
                      width,
                      height,
                    },
                  })
                );
              }
            }
          });
      }
    }
  }, []);

  const isHorizontal = () => {
    switch (tileStyle) {
      case "horizontal":
        return true;
      case "vertical":
        return false;
      default:
        return getNumbers(tile)[0] !== getNumbers(tile)[1];
    }
  };

  const getHtml = () =>
    faceDown
      ? isHorizontal()
        ? "&#127024"
        : "&#127074"
      : `&#${
          (isHorizontal() ? 127025 : 127075) +
          parseInt(getNumbers(tile).join(""), 7)
        };`;

  const handleDragStart = () => {
    dispatch(startDrag(tile));
  };
  const handleDragEnd = () => {
    dispatch(stopDrag());
    const { x, y, width, height } = ref.current.getBoundingClientRect();
    dispatch(
      startDrop({
        ...tile,
        lastCoords: {
          x,
          y,
          width,
          height,
        },
      })
    );
  };

  const getVariant = () =>
    tile.id === firstInPlayline?.id ? "selected" : variant;

  return (
    <>
      {first && <DropPlaceholder digit={getNumbers(tile)[0]} first />}
      <DominoTile
        className={className}
        size={size}
        ref={ref}
        dangerouslySetInnerHTML={{ __html: getHtml() }}
        variant={getVariant()}
        animate={controls}
        drag={draggable}
        dragMomentum={false}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        dragConstraints={{
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
        }}
        dragElastic={2}
        whileHover={draggable && { scale: 1.2 }}
      />
      {last && <DropPlaceholder digit={getNumbers(tile)[1]} />}
    </>
  );
};

export default Tile;
